import React, { useEffect, useRef, useState } from 'react';
import { FiUsers } from 'react-icons/fi';
import { statsAPI } from '../../api/api';

const VISIT_COUNTED_KEY = 'sedyam_website_visit_counted';
const BADGE_SIDE_KEY = 'sedyam_customer_badge_side';
const BADGE_POSITION_KEY = 'sedyam_customer_badge_position';
const DISPLAYED_VISIT_COUNT_KEY = 'sedyam_customer_displayed_visit_count';
const BASELINE_VISIT_COUNT = 326;

let visitCountRequest = null;

const readSavedPlacement = () => {
  try {
    const savedPlacement = JSON.parse(localStorage.getItem(BADGE_POSITION_KEY) || 'null');
    if (savedPlacement && ['left', 'right'].includes(savedPlacement.side)) {
      const topRatio = Number(savedPlacement.topRatio);
      return {
        side: savedPlacement.side,
        topRatio: Number.isFinite(topRatio) && topRatio >= 0 && topRatio <= 1 ? topRatio : null,
      };
    }
    return {
      side: localStorage.getItem(BADGE_SIDE_KEY) === 'right' ? 'right' : 'left',
      topRatio: null,
    };
  } catch (_error) {
    return { side: 'left', topRatio: null };
  }
};

const readSavedVisitCount = () => {
  try {
    const savedCount = Number(localStorage.getItem(DISPLAYED_VISIT_COUNT_KEY));
    return Number.isFinite(savedCount) && savedCount >= BASELINE_VISIT_COUNT
      ? savedCount
      : BASELINE_VISIT_COUNT;
  } catch (_error) {
    return BASELINE_VISIT_COUNT;
  }
};

const getVisitCount = () => {
  if (visitCountRequest) return visitCountRequest;

  let alreadyCounted = false;
  try {
    alreadyCounted = sessionStorage.getItem(VISIT_COUNTED_KEY) === 'true';
    if (!alreadyCounted) sessionStorage.setItem(VISIT_COUNTED_KEY, 'true');
  } catch (_error) {
    // Count this page view even when browser storage is unavailable.
  }

  const request = alreadyCounted
    ? statsAPI.getWebsiteVisitCount()
    : statsAPI.registerWebsiteVisit();

  visitCountRequest = request
    .catch((error) => {
      if (!alreadyCounted) {
        try {
          sessionStorage.removeItem(VISIT_COUNTED_KEY);
        } catch (_storageError) {
          // Ignore unavailable browser storage.
        }
      }
      throw error;
    })
    .finally(() => {
      visitCountRequest = null;
    });

  return visitCountRequest;
};

export default function CustomerVisitBadge() {
  const [visitCount, setVisitCount] = useState(readSavedVisitCount);
  const [displayCount, setDisplayCount] = useState(visitCount);
  const [placement, setPlacement] = useState(readSavedPlacement);
  const [dragPosition, setDragPosition] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
  const badgeRef = useRef(null);
  const dragRef = useRef(null);
  const displayCountRef = useRef(visitCount);

  useEffect(() => {
    let isMounted = true;
    getVisitCount()
      .then((response) => {
        if (isMounted) {
          const latestCount = BASELINE_VISIT_COUNT + Number(response?.data?.count || 0);
          setVisitCount(latestCount);
          try {
            localStorage.setItem(DISPLAYED_VISIT_COUNT_KEY, String(latestCount));
          } catch (_error) {
            // Keep the current count visible when browser storage is unavailable.
          }
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const startValue = displayCountRef.current;
    if (startValue === visitCount) return undefined;

    const duration = 900;
    const startedAt = performance.now();
    let frameId;
    const animateCount = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const easedProgress = 1 - ((1 - progress) ** 3);
      const nextValue = Math.round(startValue + ((visitCount - startValue) * easedProgress));
      displayCountRef.current = nextValue;
      setDisplayCount(nextValue);
      if (progress < 1) frameId = window.requestAnimationFrame(animateCount);
    };

    frameId = window.requestAnimationFrame(animateCount);
    return () => window.cancelAnimationFrame(frameId);
  }, [visitCount]);

  useEffect(() => {
    const updateViewportHeight = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', updateViewportHeight);
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, []);

  const startDrag = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const rect = badgeRef.current?.getBoundingClientRect();
    if (!rect) return;

    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
    };
    setDragPosition({ left: rect.left, top: rect.top });
    setDragging(true);
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (Math.abs(deltaX) + Math.abs(deltaY) > 4) drag.moved = true;

    const left = Math.max(0, Math.min(window.innerWidth - drag.width, event.clientX - drag.offsetX));
    const top = Math.max(0, Math.min(window.innerHeight - drag.height, event.clientY - drag.offsetY));
    drag.left = left;
    drag.top = top;
    setDragPosition({ left, top });
  };

  const finishDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || (event?.pointerId !== undefined && drag.pointerId !== event.pointerId)) return;

    if (drag.moved) {
      const nextSide = drag.left + drag.width / 2 < window.innerWidth / 2
        ? 'left'
        : 'right';
      const maxTop = Math.max(1, window.innerHeight - drag.height);
      const nextPlacement = {
        side: nextSide,
        topRatio: Math.max(0, Math.min(1, drag.top / maxTop)),
      };
      setPlacement(nextPlacement);
      try {
        localStorage.setItem(BADGE_POSITION_KEY, JSON.stringify(nextPlacement));
        localStorage.setItem(BADGE_SIDE_KEY, nextSide);
      } catch (_error) {
        // Keep the current placement for this page even when storage is unavailable.
      }
    }

    dragRef.current = null;
    setDragPosition(null);
    setDragging(false);
  };

  const positionStyle = dragPosition
    ? { left: `${dragPosition.left}px`, top: `${dragPosition.top}px` }
    : (() => {
        const sideStyle = placement.side === 'left' ? { left: '0px' } : { right: '0px' };
        if (placement.topRatio === null) {
          return { ...sideStyle, bottom: 'calc(env(safe-area-inset-bottom, 0px) + 22px)' };
        }
        const badgeHeight = badgeRef.current?.offsetHeight || 64;
        const maxTop = Math.max(0, viewportHeight - badgeHeight);
        return { ...sideStyle, top: `${Math.min(maxTop, placement.topRatio * maxTop)}px` };
      })();
  const shapeClass = dragPosition
    ? 'rounded-full'
    : placement.side === 'left'
      ? 'rounded-r-full rounded-l-none'
      : 'rounded-l-full rounded-r-none';

  return (
    <div
      ref={badgeRef}
      role="status"
      aria-live="polite"
      aria-label={`${visitCount.toLocaleString()} customers visited`}
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      style={positionStyle}
      className={`fixed z-[70] flex touch-none select-none items-center gap-3 ${shapeClass} border border-emerald-100 bg-white/95 px-3.5 py-2.5 text-emerald-950 shadow-[0_10px_35px_rgba(6,61,37,0.2)] backdrop-blur-md ${
        dragging ? 'cursor-grabbing' : 'cursor-grab transition-[left,right,bottom,box-shadow] duration-300 hover:shadow-[0_14px_40px_rgba(6,61,37,0.28)]'
      }`}
    >
      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-700 text-white shadow-inner">
        <FiUsers className="text-lg" aria-hidden="true" />
        <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-lime-400" aria-hidden="true" />
      </span>
      <span className="pr-1 leading-tight">
        <span className="block min-w-[3ch] text-xl font-black leading-none tracking-tight text-emerald-950 [font-variant-numeric:tabular-nums]">
          {displayCount.toLocaleString()}
        </span>
        <span className="block whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700">Customers visited</span>
      </span>
    </div>
  );
}
